import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { sparklineArea, sparklineLine } from './sparkline';

/** The accent colour of a KPI card: the 4px left border. */
export type KpiStatus = 'success' | 'warn' | 'danger' | 'info' | 'secondary';

/**
 * KPI card (design handoff, "Step 10, streamlined: the dashboard"): a label,
 * a large value with a unit, an optional meta line, a status shown as a 4px
 * left border, an optional faint background sparkline and an optional
 * "View all" link. Fed with data; it never knows what it counts.
 *
 * `label` and `linkLabel` are translation keys; `value`, `unit` and `meta`
 * are display text the page has already built. The sparkline paths are
 * computed once per input change (never in a getter).
 *
 * Styling lives in `@seahorse/theme`'s `_dashboard.scss`.
 */
@Component({
  selector: 'sh-kpi-card',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [CommonModule, RouterLink, TranslateModule],
  template: `
    <div class="kpi-card" [class]="cardClass()">
      <svg
        *ngIf="hasSparkline()"
        class="kpi-card-sparkline"
        viewBox="0 0 100 40"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path [attr.d]="areaPath()" fill="currentColor" stroke="none"></path>
        <polyline
          [attr.points]="linePoints()"
          fill="none"
          stroke="currentColor"
          stroke-width="1"
          vector-effect="non-scaling-stroke"
        ></polyline>
      </svg>

      <!-- Top row: the label, with the link right-aligned on the same line
           (review 2026-09-30). -->
      <div class="kpi-card-top">
        <div class="kpi-card-label">
          <i
            *ngIf="icon()"
            class="kpi-card-icon"
            [class]="icon()"
            aria-hidden="true"
          ></i>
          <span>{{ label() | translate }}</span>
        </div>
        <a class="kpi-card-link" *ngIf="link() as target" [routerLink]="target">
          <span>{{ linkLabel() | translate }}</span>
          <i class="fa-solid fa-arrow-right" aria-hidden="true"></i>
        </a>
      </div>
      <div class="kpi-card-value">
        <span class="kpi-card-number">{{ value() }}</span>
      </div>
      <div class="kpi-card-unit" *ngIf="unit()">{{ unit() }}</div>
      <div class="kpi-card-meta" *ngIf="meta()">{{ meta() }}</div>
    </div>
  `,
})
export class KpiCardComponent {
  /** Translation key. */
  label = input.required<string>();
  value = input.required<string | number>();
  /** Unit or short description next to/under the value, e.g. "last 7 days · 12 total". */
  unit = input<string | undefined>(undefined);
  meta = input<string | undefined>(undefined);
  status = input<KpiStatus | undefined>(undefined);
  sparkline = input<number[] | undefined>(undefined);
  link = input<string | unknown[] | undefined>(undefined);
  /** Translation key of the link text. */
  linkLabel = input<string>('shell.kpi.viewAll');
  /** Font Awesome classes, e.g. `fa-light fa-ship`. */
  icon = input<string | undefined>(undefined);

  protected readonly cardClass = computed(() => {
    const status = this.status();
    return status ? `kpi-card-${status}` : '';
  });

  protected readonly linePoints = computed(() =>
    sparklineLine(this.sparkline())
  );
  protected readonly areaPath = computed(() => sparklineArea(this.sparkline()));
  protected readonly hasSparkline = computed(() => this.linePoints() !== '');
}
